import * as React from 'react';

import { FormHelperText, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { gray100, gray80, orange50, red50 } from '../../constants/colors';
import { formLabelWrapper, textMb, titleMb } from '../../constants/location';

import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

type radioProps = React.ComponentProps<typeof RadioGroup>;

type Props = radioProps & {
  labelText?: string;
  requiredText?: string;
  required: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<Record<string, unknown>>>
    | undefined;
  width?: string;
  trueOption?: string;
  falseOption?: string;
  trueOptionDescription?: string;
  falseOptionDescription?: string;
  disabled?: boolean;
  guideText?: string;
  disabledColor?: string;
};

const styles = {
  formLabelWrapper,
  guideCard: {
    '@media screen and (max-width:600px)': {
      width: '80%',
    },
    width: '480px',
  },
  guideMark: {
    color: gray80,
    cursor: 'pointer',
    height: '18px',
    marginLeft: '4px',
    verticalAlign: 'middle',
    width: '18px',
  },
  guideText: {
    color: gray100,
    fontSize: '14px',
    whiteSpace: 'pre-wrap',
  },
  guideTitle: {
    color: gray100,
    fontSize: '22px',
    marginBottom: titleMb,
    padding: 0,
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0,
    },
    marginLeft: 0,
  },
  label: {
    fontSize: '12px',
  },
  required: {
    color: red50,
    fontSize: '12px',
    marginLeft: '10px',
  },
  optionDescription: {
    fontSize: '12px',
    color: gray80,
  },
  radioLabelContainer: {
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  radio: {
    width: '42px',
    height: '42px',
    '&.Mui-checked': {
      color: orange50,
    },
  },
  labelText: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '8px',
  },
};

const CustomRedioButton = React.forwardRef(
  (props: Props, _ref: React.Ref<unknown>): React.ReactElement => {
    const {
      labelText,
      requiredText,
      required,
      error,
      helperText,
      trueOption,
      falseOption,
      trueOptionDescription,
      falseOptionDescription,
      disabled,
      disabledColor,
      ...rest
    } = props;

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography sx={textMb} align="center" component="div">
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>
          {required ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <Typography align="left" component="div">
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-form-control-label-placement"
              name="position"
              sx={{
                '& .Mui-disabled': {
                  WebkitTextFillColor: disabledColor ?? 'initial',
                },
                '&.MuiFormGroup-root': { alignContent: 'flex-start' },
              }}
              {...rest}
            >
              <FormControlLabel
                value="true"
                control={<Radio sx={styles.radio} />}
                label={
                  <Typography sx={styles.labelText}>
                    <span>{trueOption}</span>
                    {trueOptionDescription && (
                      <Typography component="span" sx={styles.optionDescription}>
                        {trueOptionDescription}
                      </Typography>
                    )}
                  </Typography>
                }
                sx={{
                  color: gray100,
                  '& .Mui-disabled': {
                    WebkitTextFillColor: disabledColor ?? 'initial',
                  },
                  '.MuiSvgIcon-root': {
                    color: disabled && disabledColor ? disabledColor : 'inherit',
                  },
                  ...styles.radioLabelContainer,
                }}
                {...(disabled && { disabled })}
              />
              <FormControlLabel
                value="false"
                control={<Radio sx={styles.radio} />}
                label={
                  <Typography sx={styles.labelText}>
                    <span>{falseOption}</span>
                    {falseOptionDescription && (
                      <Typography component="span" sx={styles.optionDescription}>
                        {falseOptionDescription}
                      </Typography>
                    )}
                  </Typography>
                }
                sx={{
                  color: gray100,
                  '& .Mui-disabled': {
                    WebkitTextFillColor: disabledColor ?? 'initial',
                  },
                  '.MuiSvgIcon-root': {
                    color: disabled && disabledColor ? disabledColor : 'inherit',
                  },
                  ...styles.radioLabelContainer,
                }}
                {...(disabled && { disabled })}
              />
            </RadioGroup>
          </FormControl>
        </Typography>
        <FormHelperText sx={styles.helper} error={error}>
          {textfieldHelper()}
        </FormHelperText>
      </Typography>
    );
  },
);
CustomRedioButton.displayName = 'CustomRedioButton';
export default CustomRedioButton;
